/**
 * The Overview tab of the Production Line detail page (production-lines):
 * the journey step the server reports — loading, checking, evidence,
 * profile, analysis (with the taste aside), recommendations, preparing,
 * card, generating, complete, needs, failed — and the activation callout.
 * Moved verbatim out of ProductionLineDetailPage.tsx.
 */

import type { Dispatch, ReactNode, SetStateAction } from 'react';

import type {
  MerchantCreativeRunView,
  MerchantProfileView,
  RegenerationReason,
} from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import type { IntakeJourney } from '../../merchant/data.js';
import { formatRelative, type LineSummary } from '../../merchant/model.js';
import {
  Button,
  ButtonLink,
  Callout,
  Card,
  EmptyState,
  Eyebrow,
  Pix,
  Poster,
  SoonBadge,
  Tide,
  WarningIcon,
  type useToast,
} from '../../merchant/ui/primitives.js';
import { CardStep } from '../content-card/CardStep.js';
import { AnalysisStages } from '../analysis-progress/AnalysisStages.js';
import { ProfileStep } from './ProductionLineProfileStep.js';
import { RecommendationsStep } from './ProductionLineRecommendationsStep.js';
import { GeneratingStep } from './ProductionLineGeneratingStep.js';
import { CompleteStep } from './ProductionLineCompleteStep.js';
import { FailedStep } from './ProductionLineFailedStep.js';
import type { ConfiguredLine, Journey, LineModal } from './productionLineJourney.js';

export function ProductionLineOverviewTab({
  copy,
  language,
  productionLineId,
  journey,
  view,
  journeyState,
  workspaceName,
  domain,
  prompt,
  title,
  summary,
  realLine,
  latestRun,
  online,
  busy,
  selectedDirection,
  setSelectedDirection,
  askingAngle,
  setAskingAngle,
  setAnotherVersion,
  setModal,
  startVideo,
  makeIt,
  askAngle,
  makeAnotherOnLine,
  toast,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly productionLineId: string;
  readonly journey: Journey;
  readonly view: MerchantProfileView | null;
  readonly journeyState: IntakeJourney;
  readonly workspaceName: string;
  readonly domain: string;
  readonly prompt: string;
  readonly title: string;
  readonly summary: LineSummary | null;
  readonly realLine: ConfiguredLine | null;
  readonly latestRun: MerchantCreativeRunView | null;
  readonly online: boolean;
  readonly busy: string | null;
  readonly selectedDirection: string | null;
  readonly setSelectedDirection: Dispatch<SetStateAction<string | null>>;
  readonly askingAngle: boolean;
  readonly setAskingAngle: Dispatch<SetStateAction<boolean>>;
  readonly setAnotherVersion: Dispatch<SetStateAction<boolean>>;
  readonly setModal: Dispatch<SetStateAction<LineModal>>;
  readonly startVideo: () => void;
  readonly makeIt: () => void;
  readonly askAngle: (reason: RegenerationReason | null) => void;
  readonly makeAnotherOnLine: () => void;
  readonly toast: ReturnType<typeof useToast>;
}): ReactNode {
  return (
    <div role="tabpanel" className="mj-ws-mt-20">
      {journey === 'loading' ? (
        <Card padding={24} className="mj-ws-col">
          <Eyebrow>
            <Tide />
            {copy.line.building}
          </Eyebrow>
        </Card>
      ) : null}
      {journey === 'checking' ? (
        <Card padding={24} className="mj-ws-col">
          <Eyebrow>
            <Tide />
            {copy.line.checking}
          </Eyebrow>
          <p className="mj-ws-muted mj-ws-sm">{copy.line.checkingBody}</p>
        </Card>
      ) : null}
      {journey === 'evidence' ? (
        <EmptyState
          title={copy.line.evidenceTitle}
          body={copy.line.evidenceBody}
          action={
            <Button
              variant="primary"
              onClick={() => {
                setModal('brief');
              }}
            >
              {copy.line.editBrief}
            </Button>
          }
        />
      ) : null}
      {journey === 'profile' && view?.profile ? (
        <ProfileStep
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          domain={domain}
          prompt={prompt}
          websiteUrl={summary?.websiteUrl ?? null}
          payload={view.profile.payload}
          working={journeyState.working}
          onConfirm={() => void journeyState.approve()}
          onEdit={() => {
            setModal('edit-profile');
          }}
        />
      ) : null}
      {journey === 'analysis' && view !== null ? (
        <div className="mj-ws-g2 is-journey">
          <Card as="section" padding={24} aria-live="polite">
            <Eyebrow>
              <Tide />
              {copy.line.step2}
            </Eyebrow>
            <h2 className="mj-ws-h2 mj-ws-mt-8">
              {copy.line.analyzing(workspaceName)}
            </h2>
            <p className="mj-ws-muted mj-ws-sm" style={{ marginTop: 4 }}>
              {copy.line.analyzingBody}
            </p>
            {!online ? (
              <Callout className="mj-ws-mt-12" role="status">
                <WarningIcon />
                <span className="mj-ws-callout-copy mj-ws-sm">{copy.line.offline}</span>
              </Callout>
            ) : null}
            <AnalysisStages
              progress={view.analysis.progress}
              copy={copy}
              language={language}
              onRetry={journeyState.retryStep}
              working={journeyState.working}
            />
            <div className="mj-ws-muted mj-ws-xs mj-ws-mt-16">
              {copy.common.lastUpdated(
                formatRelative(view.analysis.progress.lastUpdatedAt, copy, language),
              )}{' '}
              · {copy.common.runsOnServers}
            </div>
          </Card>
          <TasteAside copy={copy} />
        </div>
      ) : null}
      {journey === 'recommendations' && view !== null ? (
        <RecommendationsStep
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          view={view}
          selected={selectedDirection}
          busy={busy === 'start'}
          onSelect={setSelectedDirection}
          onCreate={startVideo}
        />
      ) : null}
      {journey === 'preparing' ? (
        <div className="mj-ws-g2 is-journey">
          <Card as="section" padding={24} aria-labelledby="pc-h" aria-live="polite">
            <Eyebrow>
              <Tide />
              {copy.line.cardStep}
            </Eyebrow>
            <h2 id="pc-h" className="mj-ws-h2 mj-ws-mt-8">
              {copy.line.preparingCard}
            </h2>
            <p className="mj-ws-muted mj-ws-sm" style={{ marginTop: 4 }}>
              {copy.line.preparingCardBody}
            </p>
            <div className="mj-ws-muted mj-ws-xs mj-ws-mt-16">
              {copy.common.started(
                formatRelative(latestRun?.authorizedAt ?? '', copy, language),
              )}{' '}
              · {copy.common.runsOnServers}
            </div>
          </Card>
          <Card as="aside" className="mj-ws-preview-aside" padding={24}>
            <Poster width={180} state="placeholder" label={copy.line.previewSoon} />
          </Card>
        </div>
      ) : null}
      {journey === 'card' && latestRun?.card ? (
        <CardStep
          copy={copy}
          run={latestRun}
          busy={busy !== null}
          asking={askingAngle}
          onMakeIt={makeIt}
          onOpenAngles={() => {
            setAskingAngle(true);
          }}
          onCancelAngles={() => {
            setAskingAngle(false);
          }}
          onAskAngle={askAngle}
        />
      ) : null}
      {journey === 'generating' && latestRun !== null ? (
        <GeneratingStep
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          run={latestRun}
        />
      ) : null}
      {journey === 'complete' && latestRun !== null ? (
        <CompleteStep
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          lineTitle={title}
          lineId={productionLineId}
          run={latestRun}
          canMakeAnother={realLine?.productionLine.state === 'ACTIVE'}
          busy={busy !== null}
          onAnotherVersion={() => {
            if (realLine?.productionLine.state === 'ACTIVE') makeAnotherOnLine();
            else {
              setSelectedDirection(null);
              setAnotherVersion(true);
            }
          }}
        />
      ) : null}
      {journey === 'needs' && latestRun !== null ? (
        <Card
          as="section"
          padding={24}
          className="mj-ws-col is-attention"
          style={{ maxWidth: 720 }}
        >
          <Eyebrow>
            <Pix status="needs-input" />
            {copy.line.needsYou}
          </Eyebrow>
          <p>{latestRun.neededItem ?? copy.line.heldBody}</p>
          <div className="mj-ws-row">
            <Button
              variant="primary"
              disabled={busy !== null}
              onClick={() => {
                setSelectedDirection(null);
                setAnotherVersion(true);
              }}
            >
              {copy.line.chooseDifferent}
            </Button>
          </div>
        </Card>
      ) : null}
      {journey === 'failed' ? (
        <FailedStep
          copy={copy}
          workspaceName={workspaceName}
          reason={latestRun?.neededItem ?? null}
          busy={busy !== null}
          onRetry={startVideo}
          retryPossible={selectedDirection !== null || latestRun !== null}
          onChooseDifferent={() => {
            setSelectedDirection(null);
            setAnotherVersion(true);
          }}
          onContactSupport={() => {
            toast(copy.line.contactSupport);
          }}
        />
      ) : null}
      {realLine !== null &&
      (realLine.productionLine.state === 'RECOMMENDATION_READY' ||
        realLine.productionLine.state === 'CONFIGURED') ? (
        <Callout accent className="mj-ws-mt-20">
          <Pix status="ready-to-choose" />
          <span className="mj-ws-callout-copy mj-ws-sm">
            <strong>{copy.line.activateTitle}</strong>
          </span>
          <ButtonLink
            to={`/production-lines/${encodeURIComponent(productionLineId)}/activate`}
            small
          >
            {copy.action.recommendations}
          </ButtonLink>
        </Callout>
      ) : null}
    </div>
  );
}

function TasteAside({ copy }: { readonly copy: WorkspaceCopy }): ReactNode {
  return (
    <aside className="mj-ws-col">
      <Card as="section" padding={24} aria-labelledby="taste-soon-h">
        <Eyebrow className="is-between">
          <span className="mj-ws-row" style={{ gap: 8 }}>
            <Pix status="needs-input" />
            {copy.line.tasteOptional}
          </span>
          <SoonBadge />
        </Eyebrow>
        <h2 id="taste-soon-h" className="mj-ws-h2 mj-ws-mt-8" style={{ fontSize: 17 }}>
          {copy.line.tasteSoonTitle}
        </h2>
        <div className="mj-ws-g2 mj-ws-mt-12" style={{ gap: 12 }} aria-hidden="true">
          <button type="button" className="mj-ws-opt" disabled>
            <span className="mj-ws-look" data-look="polished" />
            <span className="mj-ws-strong">
              {copy.profile.tasteRows[0]?.left ?? ''}
            </span>
          </button>
          <button type="button" className="mj-ws-opt" disabled>
            <span className="mj-ws-look" data-look="organic" />
            <span className="mj-ws-strong">
              {copy.profile.tasteRows[0]?.right ?? ''}
            </span>
          </button>
        </div>
        <p className="mj-ws-muted mj-ws-sm mj-ws-mt-12">{copy.line.tasteSoonBody}</p>
      </Card>
    </aside>
  );
}
